<!--
  ChangelogHistorySection.vue
  
  Collapsible section for displaying older version history.
  Contains a toggle button and animated list of older versions.
  
  @component
-->
<template>
  <div class="mt-6 pt-4 border-t border-charcoal-600">
    <!-- Toggle Button -->
    <button
      type="button"
      class="w-full flex items-center justify-between px-3 py-2 rounded-lg bg-charcoal-900 hover:bg-charcoal-700 transition-colors text-sm"
      @click="$emit('toggle')"
    >
      <div class="flex items-center gap-2">
        <!-- Clock Icon -->
        <svg
          class="w-4 h-4 text-charcoal-300"
          fill="none"
          stroke="currentColor"
          viewBox="0 0 24 24"
        >
          <path
            stroke-linecap="round"
            stroke-linejoin="round"
            stroke-width="2"
            d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z"
          />
        </svg>

        <!-- Label -->
        <span class="font-semibold text-charcoal-200"> Version History </span>

        <!-- Count -->
        <span class="text-xs text-charcoal-400">
          ({{ versions.length }} {{ versions.length === 1 ? 'version' : 'versions' }})
        </span>
      </div>

      <!-- Chevron Icon -->
      <svg
        class="w-4 h-4 text-charcoal-400 transition-transform duration-200"
        :class="{ 'rotate-180': showHistory }"
        fill="none"
        stroke="currentColor"
        viewBox="0 0 24 24"
      >
        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M19 9l-7 7-7-7" />
      </svg>
    </button>

    <!-- History Content (Animated) -->
    <Transition
      enter-active-class="transition-all duration-300 ease-out"
      leave-active-class="transition-all duration-200 ease-in"
      enter-from-class="opacity-0 scale-y-95"
      enter-to-class="opacity-100 scale-y-100"
      leave-from-class="opacity-100 scale-y-100"
      leave-to-class="opacity-0 scale-y-95"
    >
      <div v-if="showHistory" class="mt-4 space-y-6 origin-top">
        <ChangelogVersionCard
          v-for="version in versions"
          :key="version.version"
          :version="version"
          :is-latest="false"
        />
      </div>
    </Transition>
  </div>
</template>

<script setup lang="ts">
import ChangelogVersionCard from './ChangelogVersionCard.vue'

/**
 * Version information structure
 */
interface VersionInfo {
  version: string
  date: string
  added: string[]
  changed: string[]
  fixed: string[]
}

/**
 * Props for the ChangelogHistorySection component
 */
interface Props {
  /** Array of older versions to display */
  versions: VersionInfo[]
  /** Whether history is currently expanded */
  showHistory: boolean
}

defineProps<Props>()

defineEmits<{
  /** Emitted when toggle button is clicked */
  toggle: []
}>()
</script>
